import { EnumStorageKeys } from '@/data/protocols/cache/keys'
import PathConstants from '@/main/routes/paths'
import { clearSessionId } from '@/data/services/login/session-id-store'
import { markNavigatingAway } from '@/infra/http/navigation-state'

export const isSafeRedirectUrl = (url: string): boolean => {
  let parsed: URL
  try {
    // Resolve tanto caminhos relativos ("/foo") quanto absolutos contra a origin atual.
    parsed = new URL(url, window.location.origin)
  } catch {
    return false
  }

  // Rejeita requisições cross-origin e pathnames que começam com "//"
  if (parsed.origin !== window.location.origin || parsed.pathname.startsWith('//')) {
    return false
  }

  // Rejeita /login como redirect_uri para evitar loop. Normaliza para cobrir variações.
  const normalizePath = (path: string) => path.toLowerCase().replace(/\/+$/, '') || '/'
  return normalizePath(parsed.pathname) !== normalizePath(PathConstants.Routes.LOGIN.value)
}

/**
 * Retorna um redirect_uri seguro ou o fallback.
 */
export const getSafeRedirectUri = (searchParams: URLSearchParams, fallback = ''): string => {
  const raw = searchParams.get('redirect_uri') || ''
  return raw && isSafeRedirectUrl(raw) ? raw : fallback
}

/**
 * Redireciona o usuário para a tela de login, com um possível redirect_uri.
 * Só anexa o returnUrl como redirect_uri se ele for um destino seguro (mesma origin e
 * não a própria /login) — isso evita o loop /login?redirect_uri=/login quando um 401
 * dispara enquanto já estamos em /login. Caso contrário, redireciona para /login "limpo".
 */
export const redirectToLogin = (returnUrl?: string): void => {
  clearSessionId()

  const loginUrl =
    returnUrl && isSafeRedirectUrl(returnUrl)
      ? `${PathConstants.Routes.LOGIN.value}?redirect_uri=${encodeURIComponent(returnUrl)}`
      : PathConstants.Routes.LOGIN.value

  // Marca a navegação full-page ANTES do replace: as requests em voo abortadas
  // por ele chegam sem resposta e não devem virar NetworkError/snackbar.
  markNavigatingAway()
  window.location.replace(loginUrl)
}

/**
 * Clears all authentication-related data and cookies from the client.
 * Note: HttpOnly cookies can only be cleared by the server.
 */
export const clearAuthData = (): void => {
  const cookies = document.cookie.split(';')
  const hostname = window.location.hostname
  const expiration = 'expires=Thu, 01 Jan 1970 00:00:00 UTC'

  cookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim()

    // Clear for current path and domain
    document.cookie = `${cookieName}=; ${expiration}; path=/;`
    document.cookie = `${cookieName}=; ${expiration}; path=/; domain=${hostname};`

    // Clear for parent domain if applicable

    const domainParts = hostname.split('.')

    if (domainParts.length > 2) {
      const parentDomain = domainParts.slice(-2).join('.')
      document.cookie = `${cookieName}=; ${expiration}; path=/; domain=.${parentDomain};`
    }
  })

  sessionStorage.clear()
  localStorage.removeItem(EnumStorageKeys.APP_LANGUAGE)
  clearSessionId()
}
